import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  Breadcrumb,
  Tab,
  Tabs,
} from "react-bootstrap";
import { Link, Navigate } from "react-router-dom";
import { usePermissions } from "carehub_auth_permissions";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../ui/Layout/Loader";
import {
  FaFileDownload,
  FaUpload,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { IoMdCopy } from "react-icons/io";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  fetchData,
  postMultipartData,
  downloadFile,
} from "../../api/apiService";

const BulkUpload = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [errorList, setErrorList] = useState([]);
  const { permissions, permissionsLoaded } = usePermissions();
  const [roles, setRoles] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "index",
    direction: "asc",
  });
  const [sortState, setSortState] = useState({
    name: "asc",
    value: "none",
  });
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const sortedRoles = [...roles].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (key === "name" || key === "value") {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) return direction === "asc" ? -1 : 1;
      if (nameA > nameB) return direction === "asc" ? 1 : -1;
      return 0;
    }
    return direction === "asc" ? 1 : -1;
  });

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const fetchedData = await fetchData("/roles?isReturnAll=true");
        setRoles(fetchedData.roles);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    loadRoles();
  }, []);

  const renderSortIcon = (field) => {
    if (sortConfig.key !== field) return <FaSort className="text-muted" />;
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      if (file.size > 1024 * 1024) {
        alert("File size exceeds 1 MB. Please select a smaller file.");
        e.target.value = "";
        return;
      }

      setSelectedFile(file);
    }
  };

  const filteredRoles = sortedRoles.filter((role) =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSampleDownload = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," + ["name,email,role,activate"].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "bulk_user_upload_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async () => {
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await postMultipartData("/users/bulkUserAdd", formData);
      console.log("response", response);

      setUploadSummary({
        inserted: response.successCount,
        errored: response.failedCount,
        fileId: response.fileId,
      });
      setSelectedFile(null);
      const fileInput = document.getElementById("fileInput");
      if (fileInput) {
        fileInput.value = "";
      }

      toast.success(`Succesfully processed the file.`);

      if (response.failedCount > 0) {
        const formattedErrors = response.failedUsers.map((user) => ({
          email: user.email,
          reason: user.reason,
        }));

        setErrorList(formattedErrors);
      } else {
        setErrorList([]);
      }
    } catch (error) {
      console.error("Import failed:", error);
      toast.error("Something went wrong while importing users.");
    }
  };

  const handleErrorDownload = async () => {
    try {
      const fileId = uploadSummary?.fileId;
      await downloadFile(`/users/bulkUserAdd/${fileId}`, "error_list.csv");
    } catch (err) {
      toast.error("Failed to download error CSV");
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setSortState({ ...sortState, [key]: direction });
  };

  return (
    <div>
      {!permissionsLoaded ? (
        <Loader />
      ) : permissions.includes("administration_bulk_user_upload") ? (
        <Container className="mt-3">
          <ToastContainer />
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/administration" className="text-dark link">
                Administration
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/user_management" className="text-dark link">
                User Management
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Bulk User Upload</Breadcrumb.Item>
          </Breadcrumb>

          <Card className="p-4 mb-4">
            <h3 className="mb-4">Bulk User Upload</h3>

            <Tabs
              defaultActiveKey="upload"
              id="bulk-upload-tabs"
              className="mb-3"
            >
              <Tab eventKey="upload" title="Bulk Upload">
                <Card className="p-4 mb-4">
                  <Row>
                    {/* LEFT: Upload */}
                    <Col md={8}>
                      <Form.Group controlId="formFile" className="mb-3">
                        <Form.Label>Select CSV File</Form.Label>
                        <Form.Control
                          type="file"
                          accept=".csv"
                          id="fileInput"
                          onChange={handleFileChange}
                        />
                      </Form.Group>
                      <Button
                        className="primary-btn"
                        disabled={!selectedFile}
                        onClick={handleImport}
                      >
                        <FaUpload className="me-2" />
                        Upload
                      </Button>
                    </Col>

                    {/* RIGHT: Sample File Download */}
                    <Col
                      md={4}
                      className="d-flex flex-column justify-content-start"
                    >
                      <Form.Label>Need a template?</Form.Label>
                      <Button variant="primary" onClick={handleSampleDownload}>
                        <FaFileDownload className="me-2" />
                        Download Template
                      </Button>
                    </Col>
                  </Row>
                </Card>

                {uploadSummary && (
                  <Card className="p-4 mb-4 shadow-sm border-0">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="mb-0">Upload Summary</h5>
                      {errorList.length > 0 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleErrorDownload}
                        >
                          <FaFileDownload className="me-2" />
                          Download Error List
                        </Button>
                      )}
                    </div>

                    {/* Left-aligned summary table */}
                    <div className="mt-3" style={{ maxWidth: "350px" }}>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th className="grey-table-head" colSpan={2}>
                              Summary
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <th className="fw-normal">
                              Successfully Inserted Records Count
                            </th>
                            <td className="fw-normal">
                              {uploadSummary.inserted}
                            </td>
                          </tr>
                          <tr>
                            <th className="fw-normal">Failed Records Count</th>
                            <td className="fw-normal">
                              {uploadSummary.errored}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    {errorList.length > 0 && (
                      <div className="mt-4">
                        <h5 className="mb-3">Error List</h5>
                        <Table bordered hover responsive>
                          <thead>
                            <tr>
                              <th className="grey-table-head">Email</th>
                              <th className="grey-table-head">Error Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {errorList.map((error, index) => (
                              <tr key={index}>
                                <td>{error.email}</td>
                                <td>{error.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card>
                )}
              </Tab>

              <Tab eventKey="roles" title="Role List">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Available Roles</h5>
                  <Form.Control
                    type="text"
                    placeholder="Search role name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ maxWidth: "250px" }}
                    size="sm"
                  />
                </div>
                <Table striped bordered>
                  <thead>
                    <tr>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("index")}
                      >
                        #
                      </th>
                      <th
                        onClick={() => handleSort("name")}
                        style={{ cursor: "pointer" }}
                      >
                        Value {renderSortIcon("name")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRoles.length > 0 ? (
                      filteredRoles.map((role, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>
                            <code className="me-2">{role.name}</code>
                            <OverlayTrigger
                              show={copiedIndex === index}
                              placement="right"
                              overlay={
                                <Tooltip id={`tooltip-${index}`}>
                                  Copied!
                                </Tooltip>
                              }
                            >
                              <span
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  navigator.clipboard.writeText(role.name);
                                  setCopiedIndex(index);
                                  setTimeout(() => setCopiedIndex(null), 1500);
                                }}
                              >
                                <IoMdCopy />
                              </span>
                            </OverlayTrigger>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} className="text-center">
                          No roles found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Tab>
            </Tabs>
          </Card>
        </Container>
      ) : (
        <Navigate to="/403" replace />
      )}
    </div>
  );
};

export default BulkUpload;
